/**
 * useSilentGuestMatches — iter203
 *
 * Admin-only hook: polls /api/groups/{group_id}/silent-guest-matches and
 * exposes the list of silent-added guests whose phones now match real
 * registered SplitEasy users. Drives both the dismissible group banner
 * AND the inline per-guest "Convert →" chip badge.
 *
 * Refresh strategy: fetch once on mount, refetch on focus, refetch on
 * explicit refresh() (e.g. after a successful conversion).
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

export default function useSilentGuestMatches(groupId, { enabled = true } = {}) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const fetchMatches = useCallback(async () => {
    if (!groupId || !enabled) return;
    setLoading(true);
    try {
      const r = await api.get(`/groups/${groupId}/silent-guest-matches`);
      setMatches(Array.isArray(r.data?.matches) ? r.data.matches : []);
    } catch {
      // Non-admins get 403 — silently treat as no matches.
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [groupId, enabled]);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  // Refetch on tab focus so admins see the badge appear as soon as a
  // silent-guest number signs up while the admin is still in the app.
  useEffect(() => {
    if (!enabled) return undefined;
    const onFocus = () => fetchMatches();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchMatches, enabled]);

  // Build a lookup map for fast inline-chip checks (guest_id → match).
  const matchByGuestId = matches.reduce((acc, m) => {
    acc[m.guest_id] = m;
    return acc;
  }, {});

  return {
    matches,
    matchByGuestId,
    loading,
    dismissed,
    setDismissed,
    refresh: fetchMatches,
  };
}
